import React, { useMemo } from 'react'
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { useDebouncedFetch } from './hooks/use-debounced-fetch'

type Props<T> = {
  label: React.ReactNode
  fetchOptions: (searchText: string) => Promise<T[]>
  selectedItems: T[]
  onSelect: (item: T) => void
  getOptionLabel: (option: T) => string
  getOptionKey: (option: T) => string | number
  getOptionDisabled?: (option: T) => boolean
  limitReached?: boolean
  limitTooltip?: string
  debounceMs?: number
  renderOption?: AutocompleteProps<T, false, false, false>['renderOption']
  "data-testid"?: string
}

const AutocompleteSingleFetchInput = <T extends object>({
  label,
  fetchOptions,
  selectedItems,
  onSelect,
  getOptionLabel,
  getOptionKey,
  getOptionDisabled,
  limitReached = false,
  limitTooltip = 'Limite máximo atingido',
  debounceMs = 1000,
  renderOption,
  "data-testid": dataTestId,
}: Props<T>) => {
  const { searchText, setSearchText, options, loading } = useDebouncedFetch(fetchOptions, debounceMs)

  const availableOptions = useMemo(
    () => options.filter(opt => !selectedItems.some(sel => getOptionKey(sel) === getOptionKey(opt))),
    [options, selectedItems]
  )

  return (
    <FormControl required>
      {typeof label === 'string' ? <FormLabel>{label}</FormLabel> : label}
      <Tooltip
        title={limitTooltip}
        placement="right"
        arrow
        disableHoverListener={!limitReached}
        disableFocusListener={!limitReached}
      >
        <Box sx={{ width: '40rem' }}>
          <Autocomplete
            data-testid={dataTestId}
            disablePortal
            disabled={limitReached}
            value={null}
            options={availableOptions}
            getOptionLabel={getOptionLabel}
            getOptionDisabled={getOptionDisabled}
            loading={loading}
            renderOption={renderOption}
            onChange={(_e, newValue) => {
              if (newValue) {
                onSelect(newValue)
                setSearchText('')
              }
            }}
            inputValue={searchText}
            onInputChange={(_e, newInputValue) => setSearchText(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Pesquisar" />
            )}
          />
        </Box>
      </Tooltip>
    </FormControl>
  )
}

export default AutocompleteSingleFetchInput
